import { BUILDING_LABELS, RESOURCES, type ResourceAmount } from '@bienenkampf/shared';
import { Button } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import { liveResources } from './ResourceBar.js';
import type { HiveView } from './types.js';

const DESCRIPTION: Record<string, string> = {
  BROOD: 'Beschleunigt jeden Bau. Brutplatz für Bienen (Phase 2).',
  HONEY_STORE: 'Lager für Nektar und Wachs.',
  POLLEN_STORE: 'Lager für Pollen.',
  WAX_GLAND: 'Mehr Wachs pro Stunde.',
  GUARD_COMB: 'Verteidigungsbonus im Stock (Phase 5).',
  WAR_COMB: 'Ausbildung von Kriegerinnen (Phase 2).',
  QUEEN_CELL: 'Zucht neuer Königinnen (Phase 8).',
  DANCE_FLOOR: 'Koordination der Sammelflüge (Phase 3).',
};

export function BuildingList({ view, now }: { view: HiveView; now: Date }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const live = liveResources(view, now);
  const enqueue = useMutation(
    trpc.hive.build.enqueue.mutationOptions({
      onSettled: () =>
        qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: view.id }) }),
    }),
  );
  const queueFull = view.queue.length >= view.queueSlots;

  return (
    <ul className="flex flex-col divide-y divide-stone-800">
      {view.buildings.map((b) => {
        const affordable = b.next ? RESOURCES.every((r) => live[r] >= b.next!.cost[r]) : false;
        const disabled = !b.next || !affordable || queueFull || enqueue.isPending;
        return (
          <li
            key={b.type}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-medium">{BUILDING_LABELS[b.type]}</span>
                <span className="text-sm text-stone-400">
                  Stufe {b.level}
                  {b.next && b.next.level !== b.level + 1 && ` (→ ${b.next.level - 1} in Queue)`}
                </span>
              </div>
              <div className="text-xs text-stone-500">{DESCRIPTION[b.type]}</div>
              {b.next && <CostLine cost={b.next.cost} have={live} seconds={b.next.timeSeconds} />}
            </div>
            <Button
              size="sm"
              disabled={disabled}
              onClick={() => enqueue.mutate({ hiveId: view.id, type: b.type })}
              title={
                !b.next
                  ? 'Höchststufe'
                  : queueFull
                    ? 'Bau-Queue voll'
                    : !affordable
                      ? 'Nicht genug Ressourcen'
                      : undefined
              }
            >
              {b.next ? `Stufe ${b.next.level}` : 'Max'}
            </Button>
          </li>
        );
      })}
      {enqueue.error && <li className="pt-2 text-sm text-red-400">{enqueue.error.message}</li>}
    </ul>
  );
}

function CostLine({
  cost,
  have,
  seconds,
}: {
  cost: ResourceAmount;
  have: ResourceAmount;
  seconds: number;
}) {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 text-xs tabular-nums">
      {RESOURCES.map((r) => (
        <span key={r} className={have[r] >= cost[r] ? 'text-stone-300' : 'text-red-400'}>
          {r === 'nectar' ? '🍯' : r === 'pollen' ? '🌼' : '🕯'} {formatNumber(cost[r])}
        </span>
      ))}
      <span className="text-stone-400">⏱ {formatDuration(seconds)}</span>
    </div>
  );
}
